import React, { useState, useEffect, useCallback } from "react";
import { PAGE_SIZE } from "../../../api";
import { fetchServerStats, fetchServers as fetchServersApi, fetchTopology as fetchTopologyApi } from "../services/CMDBService";
import Icon from "../../../components/Icon";
import Tooltip from "../../../components/Tooltip";
import { StatusBadge } from "../../../components/Badge";
import Skeleton from "../../../components/Skeleton";
import { EmptyState, ErrorState } from "../../../components/State";
import Pagination from "../../../components/Pagination";
import ServerDetailModal from "../../../components/ServerDetailModal";
import TopologyView from "../../../components/TopologyView";
import { getAssetCategory, formatCategoryLabel } from "../../../utils/assetTaxonomy";

const healthPercentFor = (asset) => {
  const value = Number(asset?.health_percent);
  return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
};

export default function CMDBView() {
  const [servers, setServers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [topology, setTopology] = useState(null);
  const [serversLoading, setServersLoading] = useState(true);
  const [topologyLoading, setTopologyLoading] = useState(false);
  const [serversError, setServersError] = useState(null);
  const [topologyError, setTopologyError] = useState(null);
  const [selectedServer, setSelectedServer] = useState(null);
  const [activeTab, setActiveTab] = useState("servers");
  const [hasServers, setHasServers] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [page, setPage] = useState(1);
  const [hoveredCard, setHoveredCard] = useState(null);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    issues: 0,
  });

  const fetchStats = useCallback(async () => {
    try {
      const data = await fetchServerStats();
      if (data) {
        setStats(data);
      }
    } catch (e) {
      console.error("Could not load server stats:", e);
    }
  }, []);

  const fetchServers = useCallback(async () => {
    setServersLoading(true);
    setServersError(null);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const data = await fetchServersApi({
        limit: PAGE_SIZE,
        offset: offset,
        search: appliedSearch,
        status: statusFilter,
      });
      const items = data?.items || [];
      const total = data?.total || 0;
      setServers(items);
      setTotalCount(total);
      if (!appliedSearch && !statusFilter) {
        setHasServers(total > 0 || items.length > 0);
      }
    } catch {
      setServersError("Could not load servers.");
    } finally {
      setServersLoading(false);
    }
  }, [page, appliedSearch, statusFilter]);

  const fetchTopology = useCallback(async () => {
    setTopologyLoading(true);
    setTopologyError(null);
    try {
      setTopology(await fetchTopologyApi());
    } catch {
      setTopologyError("Could not load topology.");
    } finally {
      setTopologyLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServers();
    fetchStats();
    const interval = setInterval(() => {
      fetchServers();
      fetchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchServers, fetchStats]);

  useEffect(() => {
    if (activeTab === "topology" && !topology) fetchTopology();
  }, [activeTab, topology, fetchTopology]);

  // Debounce search query changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (appliedSearch !== searchQuery) {
        setAppliedSearch(searchQuery);
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery, appliedSearch]);

  const handleStatusFilterChange = (val) => {
    setStatusFilter(val);
    setPage(1);
  };

  // Detect if "hosted_on" has any data
  const showHostedOn = servers.some((s) => s.hosted_on);

  const paged = servers;
  const activeCount = stats.active;
  const issueCount = stats.issues;

  const tabs = [
    { id: "servers", label: "Assets", icon: "server" },
    { id: "topology", label: "Topology", icon: "network" },
  ];

  let activeCardLabel = "";
  if (statusFilter === "active") {
    activeCardLabel = "Active";
  } else if (statusFilter === "inactive") {
    activeCardLabel = "Issues";
  } else if (statusFilter === "") {
    activeCardLabel = "Total assets";
  }

  const handleCardClick = (label) => {
    const isActive = activeCardLabel === label;
    if (isActive || label === "Total assets") {
      setStatusFilter("");
    } else if (label === "Active") {
      setStatusFilter("active");
    } else if (label === "Issues") {
      setStatusFilter("inactive");
    }
    setPage(1);
  };

  const statCards = [
    {
      label: "Total assets",
      value: stats.total,
      color: "#1D4ED8",
      bg: "#EFF6FF",
      icon: "server",
    },
    {
      label: "Active",
      value: activeCount,
      color: "#15803D",
      bg: "#F0FDF4",
      icon: "check",
    },
    {
      label: "Issues",
      value: issueCount,
      color: "#DC2626",
      bg: "#FEF2F2",
      icon: "alert",
    },
  ];

  const tableHeaders = [
    { label: "Name", tip: "CI name and identifier" },
    { label: "Hostname", tip: "Network hostname / FQDN" },
    { label: "Status", tip: "Current operational status" },
    { label: "Category", tip: "Asset category, such as Server or Router" },
    { label: "Health", tip: "Overall health percentage calculated by CMDB" },
    { label: "OS / Model", tip: "Operating system or Device model" },
    ...(showHostedOn
      ? [{ label: "Hosted on", tip: "Cloud or hosting platform" }]
      : []),
    { label: "Services", tip: "Services running on this CI" },
    { label: "Last seen", tip: "Most recent telemetry or scan update" },
  ];

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      <div
        style={{
          padding: "18px 24px 14px",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
        }}
      >
        <div style={{ marginBottom: 14 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            CMDB
          </h1>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6B7280" }}>
            Configuration Management Database — servers, services & topology
          </p>
        </div>
        {/* Stat cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: 10,
            marginBottom: 14,
          }}
        >
          {statCards.map((s) => {
            const isActive = activeCardLabel === s.label;
            const isHovered = hoveredCard === s.label;
            return (
              <div
                key={s.label}
                onMouseEnter={() => setHoveredCard(s.label)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => handleCardClick(s.label)}
                style={{
                  background: s.bg,
                  borderRadius: 8,
                  padding: "10px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  border: `1px solid ${isActive ? "#3B82F6" : isHovered ? s.color + "99" : s.bg}`,
                  boxShadow: isActive
                    ? "0 0 0 2.5px rgba(59, 130, 246, 0.4), 0 4px 12px rgba(0,0,0,0.08)"
                    : isHovered
                      ? "0 4px 10px rgba(0,0,0,0.05)"
                      : "none",
                  cursor: "pointer",
                  transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                >
                  <Icon name={s.icon} size={15} color={s.color} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      color: s.color,
                      fontWeight: 600,
                      opacity: 0.75,
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: s.color,
                      lineHeight: 1.2,
                    }}
                  >
                    {s.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #F3F4F6",
            marginBottom: activeTab === "servers" ? 12 : 0,
          }}
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setActiveTab(t.id);
                setSelectedServer(null);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                border: "none",
                background: "none",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: activeTab === t.id ? 700 : 400,
                color: activeTab === t.id ? "#1D4ED8" : "#6B7280",
                borderBottom:
                  activeTab === t.id
                    ? "2px solid #1D4ED8"
                    : "2px solid transparent",
              }}
            >
              <Icon
                name={t.icon}
                size={14}
                color={activeTab === t.id ? "#1D4ED8" : "#9CA3AF"}
              />
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "servers" && hasServers && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <div
              style={{
                flex: 1,
                minWidth: 200,
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Icon
                name="search"
                size={14}
                color="#9CA3AF"
                style={{
                  position: "absolute",
                  left: 10,
                  pointerEvents: "none",
                }}
              />
              <input
                placeholder="Search servers by name or hostname…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "7px 10px 7px 30px",
                  border: "1px solid #E5E7EB",
                  borderRadius: 7,
                  fontSize: 12,
                  outline: "none",
                  background: "#fff",
                }}
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              style={{
                padding: "7px 10px",
                border: "1px solid #E5E7EB",
                borderRadius: 7,
                fontSize: 12,
                background: "#fff",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="decommissioned">Decommissioned</option>
            </select>
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {activeTab === "servers" &&
          (serversError ? (
            <ErrorState message={serversError} onRetry={fetchServers} />
          ) : serversLoading ? (
            <div
              style={{
                padding: "16px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 7,
              }}
            >
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} height={44} style={{ borderRadius: 7 }} />
              ))}
            </div>
          ) : servers.length === 0 ? (
            hasServers ? (
              <EmptyState
                icon="search"
                title="No results found"
                subtitle="No matching items were found for your search query. Try clearing the search or adjusting filters."
              />
            ) : (
              <EmptyState
                icon="server"
                title="No servers registered"
                subtitle="No servers are registered in the CMDB."
              />
            )
          ) : (
            <div className="premium-table-container">
              <table className="premium-table">
                <thead>
                  <tr>
                    {tableHeaders.map((h) => (
                      <th key={h.label}>
                        <Tooltip text={h.tip}>
                          <span style={{ cursor: "default" }}>
                            {h.label}
                          </span>
                        </Tooltip>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paged.map((server) => {
                    const cat = getAssetCategory(server);
                    const isRouter = cat === "router";
                    const isDb = cat === "database" || cat === "db";
                    const healthPercent = healthPercentFor(server);

                    return (
                    <tr key={server.id} onClick={() => setSelectedServer(server)}>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: 6,
                              background: isRouter ? "#F3E8FF" : isDb ? "#FEF3C7" : "#EFF6FF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              border: isRouter ? "1px solid #E9D5FF" : isDb ? "1px solid #FDE68A" : "1px solid #BFDBFE",
                            }}
                          >
                            <Icon name={isRouter ? "network" : isDb ? "database" : "server"} size={13} color={isRouter ? "#6B21A8" : isDb ? "#92400E" : "#1D4ED8"} />
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#0F172A",
                              }}
                            >
                              {server.name}
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: "#64748B",
                                fontFamily: "monospace",
                              }}
                            >
                              {server.id.slice(0, 8)}…
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontFamily: "monospace", color: "#475569" }}>
                        {server.hostname}
                      </td>
                      <td>
                        <StatusBadge status={server.status} />
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.03em",
                            padding: "3px 8px",
                            borderRadius: 6,
                            background: isRouter ? "#F3E8FF" : isDb ? "#FEF3C7" : "#EFF6FF",
                            color: isRouter ? "#6B21A8" : isDb ? "#92400E" : "#1E40AF",
                            border: isRouter ? "1px solid #E9D5FF" : isDb ? "1px solid #FDE68A" : "1px solid #BFDBFE",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            textTransform: "uppercase",
                          }}
                        >
                          <Icon name={isRouter ? "network" : isDb ? "database" : "server"} size={10} color={isRouter ? "#6B21A8" : isDb ? "#92400E" : "#1E40AF"} />
                          {formatCategoryLabel(cat)}
                        </span>
                      </td>
                      <td>
                        {healthPercent === null ? (
                          <span style={{ color: "#94A3B8" }}>—</span>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 92 }}>
                            <span style={{ width: 48, height: 5, borderRadius: 3, background: "#E2E8F0", overflow: "hidden" }}>
                              <span style={{ display: "block", width: `${healthPercent}%`, height: "100%", background: healthPercent >= 80 ? "#16A34A" : healthPercent >= 60 ? "#D97706" : "#DC2626" }} />
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>{healthPercent.toFixed(1)}%</span>
                          </div>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const osVal = server.os || server.specs?.os || server.specs?.model || (server.specs?.vendor ? `${server.specs.vendor} Device` : null);
                          return osVal ? (
                            <span
                              style={{
                                fontSize: 11,
                                background: "#F1F5F9",
                                color: "#334155",
                                padding: "2px 8px",
                                borderRadius: 8,
                                border: "1px solid #E2E8F0",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <Icon name="cpu" size={10} color="#64748B" />
                              {osVal}
                            </span>
                          ) : (
                            <span style={{ color: "#CBD5E1", fontSize: 12 }}>
                              —
                            </span>
                          );
                        })()}
                      </td>
                      {showHostedOn && (
                        <td>
                          {(() => {
                            const rawHost = server.hosted_on || server.specs?.cloud_provider || server.specs?.hosted_on;
                            const hostVal = (!rawHost || rawHost.toLowerCase().includes("premise")) ? "GCP" : rawHost.toUpperCase();
                            return (
                              <span
                                style={{
                                  fontSize: 11,
                                  background: "#EFF6FF",
                                  color: "#1D4ED8",
                                  padding: "2px 8px",
                                  borderRadius: 8,
                                  border: "1px solid #BFDBFE",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <Icon name="layers" size={10} color="#1D4ED8" />
                                {hostVal}
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      <td style={{ color: "#64748B", whiteSpace: "nowrap" }}>
                        {server.last_seen_at ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              cursor: "default",
                            }}
                          >
                            <Icon name="clock" size={11} color="#64748B" />
                            {new Date(server.last_seen_at).toLocaleString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              },
                            )}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination
                page={page}
                total={totalCount}
                pageSize={PAGE_SIZE}
                onChange={setPage}
              />
            </div>
          ))}
        {activeTab === "topology" && (
          <div style={{ padding: "20px 24px" }}>
            {topologyError ? (
              <ErrorState message={topologyError} onRetry={fetchTopology} />
            ) : topologyLoading ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} height={80} style={{ borderRadius: 8 }} />
                ))}
              </div>
            ) : (
              <TopologyView topology={topology} />
            )}
          </div>
        )}
      </div>

      {selectedServer && (
        <ServerDetailModal
          server={selectedServer}
          onClose={() => setSelectedServer(null)}
        />
      )}
    </div>
  );
}
